import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, X, Plus, Minus, Download, Loader2, Check, FileText } from 'lucide-react';
import { useCart } from '../../Context/Cartcontext.jsx';
import { useUser } from '@clerk/clerk-react';
import LoginPopup from '../LoginPopup/LoginPopup.jsx';
import './Cart.css';
import { getImageUrl } from '../../utils/imageUtils';

const Cart = ({ onClose }) => {
  const { cartItems, removeFromCart, updateQuantity, clearCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn, isLoaded: userLoaded } = useUser();
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [downloadState, setDownloadState] = useState('idle');
  const [receiptId, setReceiptId] = useState(null);

  const baseURL = window.APP_CONFIG?.API_URL || "https://backendweb.properteck.lk";

  const total = cartItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);

  // Listen for clearCart event from checkout
  useEffect(() => {
    const handleClearCart = () => {
      if (clearCart) {
        clearCart();
      }
      setReceiptId(null);
      setDownloadState('idle');
    };

    window.addEventListener('clearCart', handleClearCart);
    return () => window.removeEventListener('clearCart', handleClearCart);
  }, [clearCart]);

  // Check for showLogin state from redirect
  useEffect(() => {
    if (location.state?.showLogin && !isSignedIn && userLoaded) {
      setShowLoginPopup(true);
    }
  }, [location.state, isSignedIn, userLoaded]);

  const handleItemClick = (id, type) => {
    if (onClose) onClose();
    switch (type) {
      case 'offer':
        navigate(`/offer/${id}`);
        break;
      case 'newarrival':
        navigate(`/newarrivals/${id}`);
        break;
      default:
        navigate(`/item/${id}`);
    }
  };

  const downloadCartData = async () => {
    setDownloadState('loading');
    try {
      let currentReceiptId = receiptId;
      
      // Save snapshot to backend and get receipt ID
      try {
        const snapshotPayload = {
          items: cartItems.map((i) => ({
            id: i.id,
            name: i.name,
            price: i.price,
            quantity: i.quantity || 1,
            type: i.type || 'item',
            imageUrl: i.imageUrl || ''
          })),
          itemCount: cartItems.length,
          total,
          meta: { source: 'cart-download' }
        };
        
        const response = await fetch(`${baseURL}/api/cart-downloads`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(snapshotPayload)
        });
        
        if (response.ok) {
          const result = await response.json();
          currentReceiptId = result.receiptId;
          setReceiptId(currentReceiptId);
        }
      } catch (e) {
        console.warn('Cart snapshot save failed (non-blocking):', e);
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();

      // Header
      doc.setFillColor(255, 0, 0);
      doc.rect(0, 0, 210, 25, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('PROPERTECK', 105, 16, { align: 'center' });

      // Summary with Receipt ID
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Shopping Cart Summary', 14, 40);

      // Receipt ID prominently displayed right after the headline
      if (currentReceiptId) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`Receipt ID: ${currentReceiptId.toUpperCase()}`, 14, 48);
      }

      const today = new Date();
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const yOffset = currentReceiptId ? 8 : 0;
      doc.text(`Generated on: ${today.toLocaleDateString()} at ${today.toLocaleTimeString()}`, 14, 56 + yOffset);
      doc.text(`Total Items: ${cartItems.length}`, 14, 62 + yOffset);
      doc.text(`Cart Value: ${total.toLocaleString()} LKR`, 14, 68 + yOffset);

      // Table Header
      let yPos = 83 + yOffset;
      const tableLeft = 14;
      const tableWidth = 182;
      const col1Width = 70;
      const col2Width = 35;
      const col3Width = 20;
      const col4Width = 35;
      const col5Width = 22;
      const rowHeight = 12;

      doc.setFillColor(240, 240, 240);
      doc.rect(tableLeft, yPos - 8, tableWidth, rowHeight, 'F');
      doc.setLineWidth(0.8);
      doc.setDrawColor(200, 200, 200);
      doc.rect(tableLeft, yPos - 8, tableWidth, rowHeight);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text('Product Name', tableLeft + 3, yPos - 1);
      doc.text('Unit Price', tableLeft + col1Width + 3, yPos - 1);
      doc.text('Qty', tableLeft + col1Width + col2Width + 8, yPos - 1);
      doc.text('Subtotal', tableLeft + col1Width + col2Width + col3Width + 3, yPos - 1);
      doc.text('Type', tableLeft + col1Width + col2Width + col3Width + col4Width + 3, yPos - 1);

      // Table Rows
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      yPos += 8;

      cartItems.forEach((item, index) => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }

        if (index % 2 === 0) {
          doc.setFillColor(250, 250, 250);
          doc.rect(tableLeft, yPos - 6, tableWidth, rowHeight, 'F');
        }

        doc.setLineWidth(0.3);
        doc.setDrawColor(220, 220, 220);
        doc.rect(tableLeft, yPos - 6, tableWidth, rowHeight);

        doc.line(tableLeft + col1Width, yPos - 6, tableLeft + col1Width, yPos + 6);
        doc.line(tableLeft + col1Width + col2Width, yPos - 6, tableLeft + col1Width + col2Width, yPos + 6);
        doc.line(tableLeft + col1Width + col2Width + col3Width, yPos - 6, tableLeft + col1Width + col2Width + col3Width, yPos + 6);
        doc.line(tableLeft + col1Width + col2Width + col3Width + col4Width, yPos - 6, tableLeft + col1Width + col2Width + col3Width + col4Width, yPos + 6);

        const itemName = item.name.length > 30 ? item.name.substring(0, 27) + '...' : item.name;
        doc.text(itemName, tableLeft + 3, yPos);
        doc.text(`${item.price.toLocaleString()}`, tableLeft + col1Width + 3, yPos);
        doc.text(`${item.quantity || 1}`, tableLeft + col1Width + col2Width + 10, yPos);
        doc.text(`${(item.price * (item.quantity || 1)).toLocaleString()}`, tableLeft + col1Width + col2Width + col3Width + 3, yPos);
        doc.text(item.type || 'item', tableLeft + col1Width + col2Width + col3Width + col4Width + 3, yPos);

        yPos += rowHeight;
      });

      // Summary Table (expanded to include receipt ID)
      yPos += 15;
      const summaryHeight = currentReceiptId ? 35 : 25;
      doc.setFillColor(245, 245, 245);
      doc.rect(tableLeft + tableWidth - 80, yPos - 8, 80, summaryHeight, 'F');
      doc.setLineWidth(0.5);
      doc.setDrawColor(200, 200, 200);
      doc.rect(tableLeft + tableWidth - 80, yPos - 8, 80, summaryHeight);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('CART SUMMARY', tableLeft + tableWidth - 75, yPos - 2);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      let summaryYPos = yPos + 5;

      // Receipt ID in summary box
      if (currentReceiptId) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 0, 0);
        doc.text(`Receipt: ${currentReceiptId.toUpperCase()}`, tableLeft + tableWidth - 75, summaryYPos);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        summaryYPos += 6;
      }

      doc.text(`Items: ${cartItems.length}`, tableLeft + tableWidth - 75, summaryYPos);
      doc.text(`Subtotal: ${total.toLocaleString()} LKR`, tableLeft + tableWidth - 75, summaryYPos + 6);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(`Total: ${total.toLocaleString()} LKR`, tableLeft + tableWidth - 75, summaryYPos + 12);

      // Footer with Receipt ID
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(120, 120, 120);
      doc.text('Thank you for shopping with PROPERTECK!', 105, 280, { align: 'center' });
      doc.text('This document was downloaded automatically.', 105, 285, { align: 'center' });
      
      if (currentReceiptId) {
        doc.setFont('helvetica', 'normal');
        doc.text(`Receipt ID: ${currentReceiptId.toUpperCase()}`, 105, 290, { align: 'center' });
      }

      // Use receipt ID in filename if available
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = currentReceiptId 
        ? `Receipt-${currentReceiptId.toUpperCase()}-${timestamp}.pdf`
        : `Checkout-Summary-${timestamp}.pdf`;
      
      doc.save(filename);

      setDownloadState('success');
      setTimeout(() => setDownloadState('idle'), 3000);
    } catch (error) {
      console.error('Download error:', error);
      setDownloadState('error');
      setTimeout(() => setDownloadState('idle'), 3000);
    }
  };

  const getDownloadButtonContent = () => {
    switch (downloadState) {
      case 'loading':
        return (
          <>
            <Loader2 className="animate-spin w-4 h-4" />
            Generating PDF...
          </>
        );
      case 'success':
        return (
          <>
            <Check className="w-4 h-4" />
            {receiptId ? `Downloaded ${receiptId.toUpperCase()}` : 'Done!'}
          </>
        );
      case 'error':
        return (
          <>
            <X className="w-4 h-4" />
            Failed - Try Again
          </>
        );
      default:
        return (
          <>
            <Download className="w-4 h-4" />
            Download Cart
          </>
        );
    }
  };

  const getDownloadButtonClass = () => {
    const baseClass = 'download-cart-enhanced';
    switch (downloadState) {
      case 'loading':
        return `${baseClass} loading`;
      case 'success':
        return `${baseClass} success`;
      case 'error':
        return `${baseClass} error`;
      default:
        return baseClass;
    }
  };

  const handleCheckout = () => {
    if (onClose) onClose();
    navigate('/checkout', { state: { fromCart: true } });
  };

  const handleLoginSuccess = () => {
    setShowLoginPopup(false);
    if (onClose) onClose();
    navigate('/checkout', { state: { fromCart: true } });
  };

  if (!onClose) {
    return (
      <div className="cart-icon">
        <ShoppingCart />
        {cartItems.length > 0 && <span className="cart-badge">{cartItems.length}</span>}
      </div>
    );
  }

  return (
    <>
      <div className="cart-popup">
        <div className="cart-content">
          <button className="cart-btn" onClick={onClose}>
            <X />
          </button>
          <h2>Your Cart</h2>
          {receiptId && (
            <div className="receipt-id-display">
              <span className="receipt-label">Last Receipt:</span>
              <span className="receipt-value">{receiptId.toUpperCase()}</span>
            </div>
          )}
          <div className="cart-items">
            {cartItems.length === 0 ? (
              <div className="empty-cart">
                <ShoppingCart className="empty-cart-icon" />
                <p>Cart is empty</p>
              </div>
            ) : (
              cartItems.map((item) => (
                <div key={item.id} className="cart-item">
                  <div className="clickable-area" onClick={() => handleItemClick(item.id, item.type)}>
                    <img src={getImageUrl(item.imageUrl)} alt={item.name} />
                    <div className="item-details">
                      <h4>{item.name}</h4>
                      <p>{item.price.toLocaleString()} LKR</p>
                      <div className="quantity-controls">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateQuantity(item.id, -1);
                          }}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span>{item.quantity || 1}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateQuantity(item.id, 1);
                          }}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <button className="remove-item" onClick={() => removeFromCart(item.id)}>
                    <X />
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="cart-footer">
            <div className="cart-total">
              <span>Total:</span>
              <span>{total.toLocaleString()} LKR</span>
            </div>

            {cartItems.length > 0 && (
              <div className="download-section">
                <div className="download-info">
                  <FileText className="w-5 h-5 text-blue-400" />
                  <div className="download-content">
                    <span className="download-title">Cart Receipt</span>
                    <span className="download-subtitle">
                      {receiptId ? `ID: ${receiptId.toUpperCase()}` : 'Generate PDF'}
                    </span>
                  </div>
                </div>
                <button
                  className={getDownloadButtonClass()}
                  onClick={downloadCartData}
                  disabled={downloadState === 'loading'}
                >
                  {getDownloadButtonContent()}
                </button>
              </div>
            )}
            
            <button
              className="checkout-btn"
              onClick={handleCheckout}
              disabled={cartItems.length === 0}
              title={cartItems.length === 0 ? 'Add items to proceed' : ''}
            >
              Proceed to Checkout
            </button>
            
          </div>
        </div>
      </div>

      {showLoginPopup && (
        <LoginPopup onClose={() => setShowLoginPopup(false)} onLoginSuccess={handleLoginSuccess} />
      )}
    </>
  );
};

export default Cart;